import type { NotificationStatus } from '@repo/types/enums/notificationStatus';
import type { NotificationPublic, PushSubscriptionType, ReadEntityNotificationsType, UnreadNotificationsCount } from '@repo/types/notifications';
import type { Pagination } from '@repo/types/pagination';

import { AxiosResponse } from 'axios';

import { axiosInstance } from '#api-client';

export class NotificationService {
  static async getNotifications(params?: GetNotificationParams): Promise<AxiosResponse<Array<NotificationPublic>>> {
    return await axiosInstance.get('/api/notification', {
      params: params,
    });
  }

  static async readNotifications(data?: number[]): Promise<AxiosResponse<string>> {
    const response = await axiosInstance.post('/api/notification/read', { arr: data });

    try {
      const unread = await this.getUnreadNotificationsCount().then((response) => response.data);
      const total: number = unread.reduce((acc, { count }) => acc + Number(count), 0);
      this.setBadge(total || 0);
    } catch (e) {
      console.error('Error updating unread notifications count:', e);
    }

    return response;
  }

  static async readEntityNotification(data: ReadEntityNotificationsType): Promise<AxiosResponse<string>> {
    const response = await axiosInstance.post('/api/notification/read/entity', data);

    try {
      const unread = await this.getUnreadNotificationsCount().then((response) => response.data);
      const total: number = unread.reduce((acc, { count }) => acc + Number(count), 0);
      this.setBadge(total || 0);
    } catch (e) {
      console.error('Error updating unread notifications count:', e);
    }

    return response;
  }

  static async readAllNotifications(): Promise<AxiosResponse<string>> {
    this.clearBadge();
    return await axiosInstance.post('/api/notification/readAll');
  }

  static async getUnreadNotificationsCount(): Promise<AxiosResponse<Array<UnreadNotificationsCount>>> {
    return await axiosInstance.get('/api/notification/unreadCnt');
  }

  static async subscribeWebPush(data: PushSubscriptionType): Promise<AxiosResponse<string>> {
    return await axiosInstance.post('/api/notification/subscribe', data);
  }

  static async unsubscribeWebPush(data: PushSubscriptionType): Promise<AxiosResponse<string>> {
    this.clearBadge();
    return await axiosInstance.post('/api/notification/unsubscribe', data);
  }

  static async clearBadge(): Promise<void> {
    if (typeof navigator !== 'undefined' && 'clearAppBadge' in navigator) {
      try {
        await navigator.clearAppBadge();
      } catch (error) {
        console.error('Failed to clear app badge:', error);
      }
    }
  }

  static async setBadge(count?: number): Promise<void> {
    if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
      try {
        await navigator.setAppBadge(count);
      } catch (error) {
        console.error('Failed to set app badge:', error);
      }
    }
  }
}

export interface GetNotificationParams extends Pagination {
  status?: NotificationStatus;
}
